import { Button, Modal } from 'antd';
import { map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import { BREAKPOINTS, DEFAULT_PAGE_SIZE } from '../../common/constants';
import CommonCard from '../../components/CommonCard';
import CommonTable from '../../components/CommonTable';
import EllipsisText from '../../components/EllipsisText';
import InfiniteScrollHandler from '../../components/InfiniteScrollHandler';
import {
  GET_AGENCY_SINGLE_ASSIGNMENTS,
  GET_AGENCY_USER_SINGLE_ASSIGNMENTS,
} from './graphql/Queries';

const ApproversListingModal = ({
  showModal,
  setShowModal,
  name,
  setMutationId,
  agencyId,
  mutationId,
}) => {
  const initialUserFilter = {
    filter: {
      skip: 0,
      limit: 10,
    },
    where: {
      ...(mutationId && { userId: mutationId }),
      ...(agencyId && { agencyId }),
    },
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const handleCancel = () => {
    setMutationId('');
    setShowModal(false);
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [userListData, setUserListData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(true);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [userFilter, setUserFilter] = useState(initialUserFilter);

  const [
    agencyUserSingleAssignments,
    { loading, data: agencyUserAssignmentsData },
  ] = useLazyQueryWithCallbacks(GET_AGENCY_USER_SINGLE_ASSIGNMENTS, {
    variables: { ...userFilter },
    fetchPolicy: 'network-only',
  });
  const [
    agencySingleAssignments,
    { loading: agencySingleAssignmentsLoading, data: agencyAssignmentsData },
  ] = useLazyQueryWithCallbacks(GET_AGENCY_SINGLE_ASSIGNMENTS, {
    variables: { ...userFilter },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (agencyUserAssignmentsData?.agencyUserSingleAssignments) {
      const data = agencyUserAssignmentsData?.agencyUserSingleAssignments?.data;
      const pagination = {
        ...paginationProp,
        total: agencyUserAssignmentsData?.agencyUserSingleAssignments?.total,
      };

      if (scrollFlag) {
        const datalist = [...userListData, ...data];
        setUserListData(datalist);
        setScrollFlag(false);
        if (
          datalist?.length ===
          agencyUserAssignmentsData?.agencyUserSingleAssignments?.total
        ) {
          setHasMore(false);
        }
      } else {
        const datalist = [...data];
        setUserListData(datalist);
      }
      setPaginationProp(pagination);
    }
  }, [agencyUserAssignmentsData]);

  useEffect(() => {
    if (agencyAssignmentsData?.agencySingleAssignments) {
      const data = agencyAssignmentsData?.agencySingleAssignments?.data;
      const pagination = {
        ...paginationProp,
        total: agencyAssignmentsData?.agencySingleAssignments?.total,
      };

      if (scrollFlag) {
        const datalist = [...userListData, ...data];
        setUserListData(datalist);
        setScrollFlag(false);
        if (
          datalist?.length ===
          agencyAssignmentsData?.agencySingleAssignments?.total
        ) {
          setHasMore(false);
        }
      } else {
        const datalist = [...data];
        setUserListData(datalist);
      }
      setPaginationProp(pagination);
    }
  }, [agencyAssignmentsData]);

  useEffect(() => {
    if (showModal) {
      const mutation = mutationId
        ? agencyUserSingleAssignments
        : agencySingleAssignments;
      mutation({ variables: { ...userFilter } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });
    setUserFilter({
      filter: {
        ...userFilter?.filter,
        skip,
        limit: pagination?.pageSize,
      },
      where: { ...userFilter?.where },
    });
    const mutation = mutationId
      ? agencyUserSingleAssignments
      : agencySingleAssignments;
    mutation({
      variables: {
        filter: {
          ...userFilter?.filter,
          skip,
          limit: pagination?.pageSize,
        },
        where: { ...userFilter?.where },
      },
    });
  };

  const columns = [
    {
      title: '#',
      key: 'id',
      render: (text, record, index) => {
        return <div>{userFilter?.filter?.skip + index + 1}</div>;
      },
    },
    ...(!mutationId
      ? [
          {
            title: 'USER',
            dataIndex: 'userName',
            key: 'userName',
            render: (record) => {
              return <EllipsisText text={record} />;
            },
          },
        ]
      : []),
    {
      title: 'PROJECT',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (record) => {
        return <EllipsisText text={record} />;
      },
    },
    {
      title: 'CHECKLIST',
      dataIndex: 'eqcTypeName',
      key: 'eqcTypeName',
      render: (record) => {
        return <EllipsisText text={record} />;
      },
    },
    {
      title: 'STAGE',
      dataIndex: 'stageName',
      key: 'stageName',
      render: (stageName) => {
        return <EllipsisText text={stageName} />;
      },
    },
    {
      title: 'LEVEL',
      dataIndex: 'levelName',
      key: 'levelName',
      render: (levelName) => {
        return <EllipsisText text={levelName} />;
      },
    },
  ];

  const handleRefetch = () => {
    const mutation = mutationId
      ? agencyUserSingleAssignments
      : agencySingleAssignments;
    mutation({
      variables: {
        filter: {
          ...userFilter?.filter,
          skip: userListData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
        where: { ...userFilter?.where },
      },
    });
  };

  return (
    <div id="deactive-user-modal">
      <Modal
        maskClosable={false}
        centered
        destroyOnHidden
        open={showModal}
        onCancel={() => handleCancel()}
        footer={null}
        closable={false}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('deactive-user-modal')
        }
      >
        <div id="approvers-modal-title" className="title">
          Checklist where <b>{name}</b> is Approver
        </div>
        {isDesktopViewport ? (
          <>
            <CommonTable
              id="approvers-listing-table"
              loadingData={loading || agencySingleAssignmentsLoading}
              columns={columns}
              data={userListData || []}
              paginationConfig={paginationProp}
              rowKey={(obj) => obj?.id}
              onChange={handleTableChange}
            />
          </>
        ) : (
          <InfiniteScrollHandler
            id="approvers-scroll-handler"
            scrollFlag={scrollFlag}
            loading={loading}
            refetchData={handleRefetch}
            setScrollFlag={setScrollFlag}
            hasMore={hasMore}
            dataLength={userListData?.length}
            skeletonRows={columns?.length - 2}
          >
            {map(userListData, (user, index) => {
              return (
                <CommonCard id={`approver-card-${index}`} key={user?.id}>
                  <div
                    id={`approver-card-content-${index}`}
                    className="common-card d-flex"
                  >
                    <div
                      id={`approver-index-${index}`}
                      className="mr-5 fw-medium"
                    >
                      {index + 1}.
                    </div>
                    <div id={`approver-details-${index}`}>
                      <div
                        id={`approver-project-${index}`}
                        className="card-header fw-medium"
                      >
                        <span>
                          <EllipsisText text={user?.projectName} />
                        </span>
                      </div>
                      <div
                        id={`approver-content-${index}`}
                        className="card-content text-secondary"
                      >
                        <br />
                        <div
                          id={`approver-checklist-${index}`}
                          className="mb-15 d-flex"
                        >
                          <span className="fw-medium mr-5">Checklist:</span>
                          <EllipsisText
                            className="text-secondary"
                            text={user?.eqcTypeName}
                          />
                        </div>
                        <div
                          id={`approver-stage-${index}`}
                          className="mb-15 d-flex"
                        >
                          <span className="fw-medium mr-5">Stage:</span>
                          <EllipsisText
                            className="text-secondary"
                            text={user?.stageName}
                          />
                        </div>
                        <div
                          id={`approver-level-${index}`}
                          className="mb-15 d-flex"
                        >
                          <span className="fw-medium mr-5">Level:</span>
                          <EllipsisText
                            className="text-secondary"
                            text={user?.levelName}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CommonCard>
              );
            })}
          </InfiniteScrollHandler>
        )}
        <div className="form-buttons">
          <Button
            id="approvers-modal-ok-btn"
            shape="round"
            type="primary"
            className="save-button mt-10"
            htmlType="submit"
            onClick={() => {
              setMutationId('');
              setShowModal(false);
            }}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ApproversListingModal;
